import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Header } from './view/header/Header'
import { Footer } from './view/footer/Footer'
import { ErrorHandler } from './components'
import { PrivateRoute } from './PrivateRoute'
import { HistoryQueryProvider } from './providers'

import {
  PartnerCompanyProjectsPage,
  PartnerCompanyUsersPage,
  PartnerCompanyUsersInvitationPage,
  AuthPage,
  AuthCallbackPage,
  DummyLoginPage,
  Logout,
  NotFound,
} from './view'
import { HelpdeskCompaniesPage } from './view/page/helpdesk/HelpdeskCompaniesPage'

const PAGE_SIDE_MARGIN = 16
const IPAD_PRO_MAX_WIDTH = 1024

const useStyles = makeStyles(() => ({
  root: {
    minWidth: IPAD_PRO_MAX_WIDTH - 2 * PAGE_SIDE_MARGIN,
  },
}))

const RedirectToLogin = () => (
  <Navigate to={process.env.REACT_APP_START_URL ?? `/auth/login`} />
)

const routes = [
  {
    path: '/',
    children: <RedirectToLogin />,
    protect: false,
  },
  {
    path: '/auth/login',
    children: <AuthPage />,
    description: 'ログインページ',
    protect: false,
  },
  {
    path: '/auth/callback',
    children: <AuthCallbackPage />,
    description: 'ログインページ（SSOコールバック）',
    protect: false,
  },
  {
    path: '/auth/login/dummy',
    children: <DummyLoginPage />,
    description: 'ログインページ（開発用ダミー）',
    protect: false,
    env: ['test'],
  },
  {
    path: '/logout',
    children: <Logout />,
    description: 'ログアウトページ',
    protect: false,
  },
  {
    path: '/partnerCompanies/:partnerCompanyId/projects',
    children: <PartnerCompanyProjectsPage />,
    description: '協力会社プロジェクト一覧ページ',
    protect: true,
  },
  {
    path: '/partnerCompanies/:partnerCompanyId/projects/:projectId/companyPositions/:companyPositionId/invitedUsers',
    children: <PartnerCompanyUsersPage prime={false} />,
    description: '協力会社ユーザ一覧ページ',
    protect: true,
  },
  {
    path: '/prime/projects/:projectId/partnerCompanies/:partnerCompanyId/companyPositions/:companyPositionId/invitedUsers',
    children: <PartnerCompanyUsersPage prime={true} />,
    description: '協力会社ユーザ一覧ページ（ログインユーザが元請の場合）',
    protect: true,
  },
  {
    path: '/partnerCompanies/:partnerCompanyId/projects/:projectId/companyPositions/:companyPositionId/users/invitation',
    children: <PartnerCompanyUsersInvitationPage prime={false} />,
    description: '協力会社ユーザ追加ページ',
    protect: true,
  },
  {
    path: '/prime/projects/:projectId/partnerCompanies/:partnerCompanyId/companyPositions/:companyPositionId/users/invitation',
    children: <PartnerCompanyUsersInvitationPage prime={true} />,
    description: '協力会社ユーザ追加ページ（ログインユーザが元請の場合）',
    protect: true,
  },
  {
    path: '/helpdesk/companies',
    children: <HelpdeskCompaniesPage />,
    description: 'ヘルプデスク企業検索',
    protect: true,
  },
].filter(
  // Pathに環境区分が設定されている場合は対象の環境の場合にのみ有効にする。
  (x) =>
    !x.env ||
    !process.env.REACT_APP_ENV ||
    x.env.some((env) => env === process.env.REACT_APP_ENV),
)

export const AppRoutes = (): JSX.Element => {
  const classes = useStyles()

  return (
    <HistoryQueryProvider>
      <Box className={classes.root}>
        <Header />
        <main>
          <ErrorHandler>
            <Routes>
              {routes.map(({ protect, children, path }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    protect ? (
                      <PrivateRoute>{children}</PrivateRoute>
                    ) : (
                      children
                    )
                  }
                />
              ))}

              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorHandler>
        </main>
        <Footer />
      </Box>
    </HistoryQueryProvider>
  )
}
